import React from 'react';
import { Check, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const plans = [
  {
    name: 'Starter',
    price: '4900',
    period: 'par mois',
    description: 'Parfait pour démarrer votre activité',
    features: [
      "Jusqu'à 10 packages",
      "Page d'agence personnalisée",
      'Réservations en ligne',
      'Support par email',
      'Statistiques basiques',
    ],
    notIncluded: [
      'Personnalisation avancée du thème',
      'API de réservation',
      'Support prioritaire',
      'Multi-utilisateurs',
      'Rapports avancés',
    ],
    cta: 'Commencer gratuitement',
    popular: false,
  },
  {
    name: 'Professional',
    price: '9900',
    period: 'par mois',
    description: 'Pour les agences en pleine croissance',
    features: [
      'Packages illimités',
      "Page d'agence personnalisée",
      'Réservations en ligne',
      'Support prioritaire',
      'Statistiques avancées',
      'Personnalisation avancée du thème',
      "Multi-utilisateurs (jusqu'à 5)",
      'API de réservation',
    ],
    notIncluded: [
      'Intégration CRM',
      'Formation personnalisée',
    ],
    cta: 'Essai gratuit de 14 jours',
    popular: true,
  },
  {
    name: 'Enterprise',
    price: '24900',
    period: 'par mois',
    description: 'Solutions sur mesure pour grandes agences',
    features: [
      'Tout ce qui est inclus dans Professional',
      'Utilisateurs illimités',
      'Intégration CRM',
      'Formation personnalisée',
      'Account manager dédié',
      'SLA garanti',
      'Support téléphonique 24/7',
      'Personnalisation complète',
    ],
    notIncluded: [],
    cta: 'Contacter les ventes',
    popular: false,
  },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* En-tête */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold mb-4">
              Tarifs simples et transparents
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choisissez le plan qui correspond le mieux à vos besoins. 
              Tous nos plans incluent un essai gratuit de 14 jours.
            </p>
          </div>

          {/* Grille des prix */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${
                  plan.popular ? 'ring-2 ring-blue-600' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-blue-600 text-white px-4 py-1 text-sm">
                    Populaire
                  </div>
                )}

                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-4xl font-bold">
                      {parseInt(plan.price).toLocaleString('fr-FR')} DA
                    </span>
                    <span className="text-gray-600"> {plan.period}</span>
                  </div>

                  <button
                    className={`w-full py-3 px-4 rounded-lg mb-8 ${
                      plan.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan.cta}
                  </button>

                  <div className="space-y-4">
                    <p className="font-medium">Inclus :</p>
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-center text-gray-600">
                        <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}

                    {plan.notIncluded.length > 0 && (
                      <>
                        <p className="font-medium mt-6">Non inclus :</p>
                        {plan.notIncluded.map((feature) => (
                          <div key={feature} className="flex items-center text-gray-400">
                            <X className="w-5 h-5 mr-2 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-center mb-12">
              Questions fréquentes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold mb-2">
                  Puis-je changer de plan à tout moment ?
                </h3>
                <p className="text-gray-600">
                  Oui, vous pouvez changer de plan à tout moment. La différence sera calculée au prorata.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold mb-2">
                  Que se passe-t-il après la période d'essai ?
                </h3>
                <p className="text-gray-600">
                  Vous serez facturé uniquement si vous décidez de continuer. Pas de mauvaise surprise.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold mb-2">
                  Y a-t-il des frais cachés ?
                </h3>
                <p className="text-gray-600">
                  Non, nos prix sont transparents. Pas de frais supplémentaires.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold mb-2">
                  Comment fonctionne le support ?
                </h3>
                <p className="text-gray-600">
                  Nous offrons un support par email pour tous les plans, et un support prioritaire 24/7 pour les plans supérieurs.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-20 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Vous avez d'autres questions ?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Notre équipe est là pour vous aider à choisir la meilleure solution.
            </p>
            <button className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700">
              Contactez-nous
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}